<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type TableRow } from '../utils/csvFormatting'
import { type IconCellData } from '../composables/useIconGrid'

const props = withDefaults(defineProps<{
  tableData: TableRow[]
  getIcon: (timeStr: string) => IconCellData
  isPast?: (timeStr: string) => boolean
  /** Shorter viewBox for the combined main view (#31). Reduces vertical
   *  pixels in the plot area while keeping the icon row + margins intact,
   *  so the chart sits under the short-text summary without dominating. */
  compact?: boolean
}>(), {
  isPast: () => () => false,
  compact: false,
})

const { t: $t } = useI18n()

const CHART_HOURS = ['00:00', '03:00', '06:00', '09:00', '12:00', '15:00', '18:00', '21:00', '24:00']
const VB_W = 400
// Standard view keeps the original 400×320 viewBox. Compact mode drops the
// viewBox height so the SVG's aspect becomes wider (shorter-for-same-width
// once preserveAspectRatio='xMidYMid meet' kicks in). Margins stay fixed;
// only the plot area shrinks.
const VB_H = props.compact ? 220 : 320
const M = { top: 16, right: 40, bottom: 92, left: 40 }
const PLOT_W = VB_W - M.left - M.right
const PLOT_H = VB_H - M.top - M.bottom
const ICON_SIZE = 26

// 8-point compass → degrees the wind is FROM. Arrow rotation points the way
// the wind is blowing TO (from-angle + 180°).
const FROM_ANGLES: Record<string, number> = {
  N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315
}
const windRotation = (dir: string): number =>
  ((FROM_ANGLES[dir.toUpperCase()] ?? 0) + 180) % 360

interface Point {
  x: number; temp: number | null; rh: number | null; hour: number; timeStr: string
  windDir: string; beaufort: string
}

const points = computed<Point[]>(() => {
  const byTime = new Map<string, TableRow>()
  for (const row of props.tableData) byTime.set(row.time, row)

  return CHART_HOURS.map((timeStr) => {
    const hour = parseInt(timeStr.split(':')[0], 10)
    const x = M.left + (hour / 24) * PLOT_W
    const row = byTime.get(timeStr)
    // Plot raw numeric values (1-decimal precision from backend) rather than
    // reparsing the rounded display string — otherwise the chart loses the
    // precision in csvFormatting.ts's Math.round() on row.temp/humidity.
    const tempRaw = row && Number.isFinite(row.tempRaw) ? (row.tempRaw as number) : NaN
    const rhRaw = row && Number.isFinite(row.humidityRaw) ? (row.humidityRaw as number) : NaN
    return {
      x,
      temp: Number.isFinite(tempRaw) ? tempRaw : null,
      rh: Number.isFinite(rhRaw) ? rhRaw : null,
      hour,
      timeStr,
      windDir: (row?.windDir || '').trim(),
      beaufort: (row?.windSpeed || '').trim()
    }
  })
})

const tempPoints = computed(() => points.value.filter(p => p.temp !== null))
const rhPoints = computed(() => points.value.filter(p => p.rh !== null))

// Temperature axis: fixed 10°C window (step 2°C), falls back to 25°C (step 5°C)
// when the data spans more than 10°C. Bottom padding is claimed opportunistically
// from any top slack — if the data swings a full 10°C and the window is already
// tight we leave zero bottom pad rather than jumping to the 25°C fallback (which
// would make the curve look tiny). Caps at 2°C (one step) so we never waste more
// than one tick of space on headroom.
// Top padding is not enforced; a spline that overshoots above the top tick
// clips cleanly against the SVG edge.
interface TempAxis { lo: number; hi: number; step: number; ticks: number[] }

function makeTempAxis(min: number, max: number): TempAxis {
  const build = (lo: number, hi: number, step: number): TempAxis => {
    const ticks: number[] = []
    for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(v)
    return { lo, hi, step, ticks }
  }
  // 10°C window — claim up to 2°C bottom pad from top slack.
  const baseLo10 = Math.floor(min / 2) * 2
  if (baseLo10 + 10 >= max) {
    const topSlack = baseLo10 + 10 - max
    const shiftDown = Math.min(Math.floor(topSlack / 2) * 2, 2)
    const lo = baseLo10 - shiftDown
    return build(lo, lo + 10, 2)
  }
  // 25°C fallback — same opportunistic bottom-pad logic, capped at 5°C.
  let baseLo25 = Math.floor(min / 5) * 5
  let baseHi25 = baseLo25 + 25
  while (baseHi25 < max) baseHi25 += 5
  const topSlack25 = baseHi25 - max
  const shift25 = Math.min(Math.floor(topSlack25 / 5) * 5, 5)
  return build(baseLo25 - shift25, baseHi25 - shift25, 5)
}

const tempAxis = computed<TempAxis>(() => {
  const vals = tempPoints.value.map(p => p.temp as number)
  if (vals.length === 0) return { lo: 0, hi: 10, step: 2, ticks: [0, 2, 4, 6, 8, 10] }
  return makeTempAxis(Math.min(...vals), Math.max(...vals))
})

// Relative humidity axis is fixed at 0–100% with 20% ticks (6 lines, matching
// the temperature axis tick count).
const RH_LO = 0
const RH_HI = 100
const rhTicks = [0, 20, 40, 60, 80, 100]

function smoothPath(pts: Array<{ x: number; y: number }>): string {
  if (pts.length === 0) return ''
  if (pts.length === 1) return `M ${pts[0].x},${pts[0].y}`
  if (pts.length === 2) return `M ${pts[0].x},${pts[0].y} L ${pts[1].x},${pts[1].y}`

  const t = 1 / 6
  let d = `M ${pts[0].x},${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] || pts[i + 1]
    const c1x = p1.x + (p2.x - p0.x) * t
    const c1y = p1.y + (p2.y - p0.y) * t
    const c2x = p2.x - (p3.x - p1.x) * t
    const c2y = p2.y - (p3.y - p1.y) * t
    d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`
  }
  return d
}

const yTemp = (v: number): number => {
  const { lo, hi } = tempAxis.value
  const ratio = hi === lo ? 0.5 : (v - lo) / (hi - lo)
  return M.top + PLOT_H - ratio * PLOT_H
}

const yRh = (v: number): number => {
  const ratio = (v - RH_LO) / (RH_HI - RH_LO)
  return M.top + PLOT_H - ratio * PLOT_H
}

const tempPath = computed(() =>
  smoothPath(tempPoints.value.map(p => ({ x: p.x, y: yTemp(p.temp as number) })))
)

const rhPath = computed(() =>
  smoothPath(rhPoints.value.map(p => ({ x: p.x, y: yRh(p.rh as number) })))
)

const iconRow = computed(() =>
  points.value.map((p) => {
    const icon = props.getIcon(p.timeStr)
    return {
      x: p.x,
      hasData: icon.hasData,
      code: icon.icon,
      label: icon.timeLabel,
      isPast: props.isPast(p.timeStr),
      windDir: p.windDir,
      beaufort: p.beaufort
    }
  })
)

const hasAnyData = computed(() => tempPoints.value.length > 0 || rhPoints.value.length > 0)

// Right edge of the shaded "past" region: the rightmost past point's x (no past → 0)
const pastBoundaryX = computed(() => {
  let maxX = 0
  for (const p of points.value) {
    if (props.isPast(p.timeStr) && p.x > maxX) maxX = p.x
  }
  return maxX
})
</script>

<template>
  <div v-if="hasAnyData" class="forecast-chart">
    <div class="chart-legend">
      <span class="legend-item">
        <span class="swatch swatch-temp"></span>{{ $t('forecast.chartLegend.temperature') }}
      </span>
      <span class="legend-item">
        <span class="swatch swatch-rh"></span>{{ $t('forecast.chartLegend.humidity') }}
      </span>
    </div>
    <!-- Wind legend moved to the forecast-main-footer row next to
         "About this forecast" in Forecast.vue — see that file. -->

    <svg
      :viewBox="`0 0 ${VB_W} ${VB_H}`"
      preserveAspectRatio="xMidYMid meet"
      class="chart-svg"
      role="img"
      :aria-label="$t('forecast.chart')"
    >
      <!-- Past-time region shading -->
      <rect
        v-if="pastBoundaryX > M.left"
        :x="M.left"
        :y="M.top"
        :width="pastBoundaryX - M.left"
        :height="PLOT_H"
        class="past-region"
      />

      <!-- Baseline -->
      <line
        :x1="M.left" :x2="VB_W - M.right"
        :y1="M.top + PLOT_H" :y2="M.top + PLOT_H"
        class="chart-axis"
      />

      <!-- Left y-axis (temperature) -->
      <line
        v-if="tempPoints.length"
        :x1="M.left" :x2="M.left"
        :y1="M.top" :y2="M.top + PLOT_H"
        class="chart-axis-temp"
      />

      <!-- Right y-axis (relative humidity) -->
      <line
        v-if="rhPoints.length"
        :x1="VB_W - M.right" :x2="VB_W - M.right"
        :y1="M.top" :y2="M.top + PLOT_H"
        class="chart-axis-rh"
      />

      <!-- Gridlines + temperature ticks (left axis) -->
      <template v-if="tempPoints.length">
        <g v-for="v in tempAxis.ticks" :key="`t-${v}`">
          <line
            :x1="M.left" :x2="VB_W - M.right"
            :y1="yTemp(v)" :y2="yTemp(v)"
            class="chart-grid"
          />
          <line
            :x1="M.left - 3" :x2="M.left"
            :y1="yTemp(v)" :y2="yTemp(v)"
            class="chart-tick-temp"
          />
          <text
            :x="M.left - 5" :y="yTemp(v) + 3"
            class="axis-label axis-label-temp"
            text-anchor="end"
          >{{ v }}℃</text>
        </g>
      </template>

      <!-- RH ticks (right axis) -->
      <template v-if="rhPoints.length">
        <g v-for="v in rhTicks" :key="`r-${v}`">
          <line
            :x1="VB_W - M.right" :x2="VB_W - M.right + 3"
            :y1="yRh(v)" :y2="yRh(v)"
            class="chart-tick-rh"
          />
          <text
            :x="VB_W - M.right + 5" :y="yRh(v) + 3"
            class="axis-label axis-label-rh"
            text-anchor="start"
          >{{ v }}%</text>
        </g>
      </template>

      <!-- RH line (dashed, secondary) -->
      <path
        v-if="rhPoints.length > 1"
        :d="rhPath"
        class="line-rh"
      />
      <circle
        v-for="(p, i) in rhPoints"
        :key="`rh-${i}`"
        :cx="p.x" :cy="yRh(p.rh as number)"
        r="2"
        class="dot-rh"
      />

      <!-- Temperature line (solid, prominent) -->
      <path
        v-if="tempPoints.length > 1"
        :d="tempPath"
        class="line-temp"
      />
      <circle
        v-for="(p, i) in tempPoints"
        :key="`temp-${i}`"
        :cx="p.x" :cy="yTemp(p.temp as number)"
        r="3"
        class="dot-temp"
      />

      <!-- Time labels, weather icons, wind direction + Beaufort on x-axis -->
      <g v-for="(icn, i) in iconRow" :key="`icon-${i}`" :class="{ 'icon-past': icn.isPast }">
        <text
          :x="icn.x"
          :y="M.top + PLOT_H + 14"
          class="hour-label"
          text-anchor="middle"
        >{{ icn.label }}</text>
        <image
          v-if="icn.hasData"
          :href="`/icons/qweather/${icn.code}.svg`"
          :x="icn.x - ICON_SIZE / 2"
          :y="M.top + PLOT_H + 22"
          :width="ICON_SIZE"
          :height="ICON_SIZE"
        />
        <g
          v-if="icn.windDir"
          :transform="`translate(${icn.x}, ${M.top + PLOT_H + 62}) rotate(${windRotation(icn.windDir)})`"
        >
          <path d="M 0,-6 L -4,4 L 0,1 L 4,4 Z" class="wind-arrow" />
        </g>
        <text
          v-if="icn.beaufort"
          :x="icn.x"
          :y="M.top + PLOT_H + 82"
          class="beaufort-label"
          text-anchor="middle"
        >{{ icn.beaufort }}</text>
      </g>
    </svg>
  </div>
  <div v-else class="no-chart-data">{{ $t('forecast.noChartData') }}</div>
</template>

<style scoped>
.forecast-chart {
  width: 100%;
  padding: 0 4px;
  color: rgb(var(--v-theme-on-surface));
}

.chart-svg {
  width: 100%;
  height: auto;
  display: block;
  overflow: visible;
}

/* The qweather SVGs use fill="currentColor", but SVG <image> loads them as
 * isolated documents — currentColor resolves to the loaded SVG's own root
 * (black), not this chart's color. Mirror the grid icons' brightness/invert
 * trick so they show up on dark backgrounds. */
.chart-svg image {
  filter: brightness(0) invert(1);
}
[data-theme="light"] .chart-svg image {
  filter: brightness(0) invert(0.2);
}

.chart-axis {
  stroke: rgba(128, 128, 128, 0.4);
  stroke-width: 1;
}

.chart-grid {
  stroke: rgba(128, 128, 128, 0.15);
  stroke-width: 1;
}

.chart-tick-temp {
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 1;
  opacity: 0.7;
}

.chart-axis-temp {
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 1;
  opacity: 0.7;
}

.chart-tick-rh {
  stroke: rgba(128, 128, 128, 0.7);
  stroke-width: 1;
}

.chart-axis-rh {
  stroke: rgba(128, 128, 128, 0.7);
  stroke-width: 1;
}

.axis-label {
  font-size: 11px;
  fill: currentColor;
  opacity: 0.7;
}

.axis-label-temp {
  fill: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.axis-label-rh {
  fill: currentColor;
  opacity: 0.55;
}

.line-temp {
  fill: none;
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.dot-temp {
  fill: rgb(var(--v-theme-primary));
}

.line-rh {
  fill: none;
  stroke: rgba(128, 128, 128, 0.7);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 4 3;
}

.dot-rh {
  fill: rgba(128, 128, 128, 0.7);
}

.hour-label {
  font-size: 9px;
  fill: currentColor;
}

.wind-arrow {
  fill: currentColor;
}

.beaufort-label {
  font-size: 10px;
  font-weight: 600;
  fill: currentColor;
}

.past-region {
  fill: rgba(128, 128, 128, 0.12);
}

.icon-past {
  opacity: 0.5;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin: 0;
  font-size: 11px;
  color: currentColor;
  opacity: 0.8;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  display: inline-block;
  width: 18px;
  height: 3px;
  border-radius: 2px;
}

.swatch-temp {
  background: rgb(var(--v-theme-primary));
}

.swatch-rh {
  background: repeating-linear-gradient(
    to right,
    rgba(128, 128, 128, 0.7) 0,
    rgba(128, 128, 128, 0.7) 4px,
    transparent 4px,
    transparent 7px
  );
  height: 2px;
}

/* Compact arrow glyph swatch for the wind-legend entry — matches the
 * style of the arrows rendered under the chart's x-axis. Not a colour
 * swatch because the wind indicator is a shape, not a line. */
.swatch-wind {
  width: auto;
  height: auto;
  background: transparent;
  font-size: 11px;
  line-height: 1;
  transform: translateY(-1px);
  opacity: 0.8;
}

.no-chart-data {
  text-align: center;
  padding: 24px;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.7;
}
</style>
